// 요일 다국어 텍스트
const dayNamesLocalized = {
  ko: ['일', '월', '화', '수', '목', '금', '토'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
};

// 월 다국어 텍스트 (영어용)
const monthNamesEn = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

/**
 * 시간 포맷 유틸리티 함수 (서버의 ISO 포맷을 벳지용으로 가공)
 * @param liveTime - ISO 형식의 라이브 시간 문자열
 * @param language - 언어 설정 ('ko' | 'en')
 * @param endTime - ISO 형식의 라이브 종료 시간 문자열 (선택사항)
 * @returns 포맷된 시간 문자열
 */
export const formatLiveTime = (liveTime: string, language: 'ko' | 'en' = 'ko', endTime?: string): string => {
  try {
    const targetDate = new Date(liveTime);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const targetDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    
    // 현재 시간이 라이브 시간 이후라면
    if (now >= targetDate) {
      // 종료 시간이 있고, 현재가 라이브 중인 경우
      if (endTime) {
        const endDate = new Date(endTime);
        
        // 이미 종료된 경우
        if (now >= endDate) {
          return 'LIVE';
        }
        
        // 종료 10분 전부터는 종료까지 남은 시간 표시
        const endDiffMs = endDate.getTime() - now.getTime();
        const tenMinutesMs = 10 * 60 * 1000; // 10분을 밀리초로
        
        if (endDiffMs <= tenMinutesMs) {
          const remainingMinutes = Math.ceil(endDiffMs / (1000 * 60));
          
          if (language === 'ko') {
            return `종료임박 ${remainingMinutes.toString().padStart(2, '0')}분`;
          } else {
            return `Ending ${remainingMinutes.toString().padStart(2, '0')}min`;
          }
        }
      }
      
      return 'LIVE';
    }
    
    // 오늘이고 미래 시간이면 남은 시간 계산 (xxh xxm 형태)
    if (targetDay.getTime() === today.getTime()) {
      const diffMs = targetDate.getTime() - now.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffHours > 0) {
        return `${diffHours}h ${diffMinutes}m`;
      } else {
        return `${diffMinutes}m`;
      }
    }
    
    // 오늘이 아니면 날짜 형태로 표시
    const month = targetDate.getMonth() + 1;
    const date = targetDate.getDate();
    const dayNames = dayNamesLocalized[language];
    const dayName = dayNames[targetDate.getDay()];
    const hours = targetDate.getHours().toString().padStart(2, '0');
    const minutes = targetDate.getMinutes().toString().padStart(2, '0');
    
    if (language === 'ko') {
      // 한국어: 07/17(금) 13:00
      const monthStr = month.toString().padStart(2, '0');
      const dateStr = date.toString().padStart(2, '0');
      return `${monthStr}/${dateStr}(${dayName}) ${hours}:${minutes}`;
    } else {
      // 영어: Jul 17(Fri) 13:00 (KR TIME)
      const monthName = monthNamesEn[targetDate.getMonth()];
      return `${monthName} ${date}(${dayName}) ${hours}:${minutes} (KR TIME)`;
    }
  } catch (error) {
    return liveTime; // 오류 발생시 원본 반환
  }
};

/**
 * 날짜 포맷팅 함수 (ISO string을 yy.mm.dd hh:MM 형태로 변환)
 * @param isoString - ISO 형식의 날짜 문자열
 * @returns 포맷된 날짜 문자열
 */
export const formatDate = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    const year = date.getFullYear().toString().slice(2); // 뒤 2자리
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}.${month}.${day} ${hours}:${minutes}`;
  } catch (error) {
    return isoString;
  }
};

/**
 * 경매 기간 포맷팅 함수
 * @param startDate - 시작 날짜 ISO 문자열
 * @param endDate - 종료 날짜 ISO 문자열
 * @returns 포맷된 경매 기간 문자열
 */
export const formatAuctionPeriod = (startDate: string, endDate: string): string => {
  const start = formatDate(startDate).split(' ');
  const end = formatDate(endDate).split(' ');
  const startDatePart = start[0]; // yy.mm.dd
  const startTimePart = start[1]; // hh:MM
  const endDatePart = end[0]; // yy.mm.dd
  const endTimePart = end[1]; // hh:MM

  // 시작/종료 모두 동일 포맷(yy.mm.dd hh:MM)으로 표시
  return `${startDatePart} ${startTimePart} ~ ${endDatePart} ${endTimePart}`;
};

/**
 * 시간 차이를 상대적 시간으로 표시하는 함수
 * @param createdTime - ISO 형식의 생성 시간 문자열
 * @returns 상대적 시간 문자열 (예: 3분전, 2시간전, 1일전)
 */
export const getRelativeTime = (createdTime: string): string => {
  try {
    const created = new Date(createdTime);
    const now = new Date();
    const diffMs = now.getTime() - created.getTime();

    // 밀리초를 분으로 변환
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    if (diffMinutes < 1) {
      return '방금전';
    } else if (diffMinutes < 60) {
      return `${diffMinutes}분전`;
    }

    // 시간 단위
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      return `${diffHours}시간전`;
    }

    // 일 단위
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) {
      return `${diffDays}일전`;
    }

    // 월 단위
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) {
      return `${diffMonths}개월전`;
    }

    // 년 단위
    const diffYears = Math.floor(diffMonths / 12);
    return `${diffYears}년전`;
  } catch (error) {
    return createdTime;
  }
};
